import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CircularProgress, Alert, Box, Typography, Button } from '@mui/material'
import { PRIVATE_PATHS } from '../../../../utils/paths'
import APIService from '../../../../services/APIService'
import { getSharePointRedirectUri } from '../../../../utils/sharepoint'
import LocalStorageService from '../../../../services/LocalStorageService'
import { KEYS } from '../../../../utils/localStorage'

const SharePointCallback = () => {
  const api = APIService()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      if (error) {
        setError(`Authorisation failed: ${errorDescription || error}`)
        setLoading(false)
        return
      }

      if (!code || !state) {
        setError('Invalid callback parameters')
        setLoading(false)
        return
      }

      try {
        // First try to decode state from the URL parameter
        let decodedState = {}
        let isReauth = false

        if (state) {
          try {
            decodedState = JSON.parse(atob(state))
            isReauth = decodedState.is_reauth === true
          } catch (e) {
            console.error('Failed to decode state parameter:', e)
          }
        }

        // For reauth flow, use building_passport_id from decoded state
        // For initial integration, try session storage first, then decoded state
        let integrationState = {}

        if (isReauth && decodedState.building_passport_id) {
          integrationState = {
            building_passport_id: decodedState.building_passport_id,
            is_reauth: true
          }
        } else {
          // Try session storage first (initial integration flow)
          integrationState = JSON.parse(
            sessionStorage.getItem('sharepoint_integration_state') || '{}'
          )

          // If not found in session storage, use decoded state
          if (!integrationState.building_passport_id && decodedState.building_passport_id) {
            integrationState = {
              building_passport_id: decodedState.building_passport_id,
              view_name: decodedState.view_name || 'SharePoint Documents',
              site_url: decodedState.site_url || ''
            }
          }
        }

        if (!integrationState.building_passport_id) {
          throw new Error('Integration state not found in session or state parameter')
        }

        // Get the token from localStorage using the correct key
        const token = LocalStorageService.get(KEYS.token)
        console.log('Token check - token exists?', !!token)
        console.log('Token value (first 20 chars):', token ? token.substring(0, 20) + '...' : 'null')

        if (!token) {
          console.warn('No authentication token found in localStorage')
          // If no token, try to send a message to the parent window to handle it
          if (window.opener) {
            window.opener.postMessage({
              type: 'sharepoint_auth_token_missing',
              error: 'Your session has expired. Please close this window and try the SharePoint integration again after logging in.'
            }, window.location.origin)

            // Also try wildcard origin
            window.opener.postMessage({
              type: 'sharepoint_auth_token_missing',
              error: 'Your session has expired. Please close this window and try the SharePoint integration again after logging in.'
            }, '*')

            // Show user-friendly message in this window
            throw new Error('Your session has expired. Please close this window, log in to the main application, and try the SharePoint integration again.')
          } else {
            throw new Error('Authentication session expired. Please return to the main application, log in, and try again.')
          }
        }

        console.log('Token found, proceeding with exchange-code API call...')

        // Exchange the code for tokens first
        const response = await api.post(`/building-passport/${integrationState.building_passport_id}/sharepoint/exchange-code`, {
          code,
          state,
          // Must match exactly what was sent in the initial request
          redirect_uri: getSharePointRedirectUri()
        })

        const data = response.data?.data || response.data

        console.log('SharePoint callback response:', response)
        console.log('Extracted data:', data)
        console.log('Window opener exists?', !!window.opener)
        console.log('Window origin:', window.location.origin)

        if (data && (data.accessToken || data.access_token)) {
          // Handle both camelCase and snake_case
          const messageData = {
            type: 'sharepoint_integration_success',
            access_token: data.accessToken || data.access_token,
            refresh_token: data.refreshToken || data.refresh_token,
            expires_in: data.expiresIn || data.expires_in || 3600,
            site_id: data.siteId || data.site_id,
            drive_id: data.driveId || data.drive_id,
            is_reauth: data.isReauth || data.is_reauth || integrationState.is_reauth || false
          }

          console.log('Sending message to parent window:', messageData)
          console.log('Target origin:', window.location.origin)

          // Send token data back to the parent window for folder selection
          if (window.opener) {
            try {
              // Try sending the message
              window.opener.postMessage(messageData, window.location.origin)
              console.log('Message sent successfully!')

              // Also try with wildcard origin as a fallback
              window.opener.postMessage(messageData, '*')
              console.log('Fallback message sent with * origin')
            } catch (e) {
              console.error('Error sending message:', e)
            }

            // Give a small delay to ensure message is sent before closing
            setTimeout(() => {
              console.log('Closing window...')
              window.close()
            }, 500) // Increased delay
          } else {
            console.log('No window.opener found, navigating directly')
            // If no opener, we're not in a popup - navigate directly
            navigate(PRIVATE_PATHS.bpDetails.replace(':id', integrationState.building_passport_id))
          }
        } else {
          console.error('No data received from exchange-code endpoint:', response)
          setError(data?.message || 'Failed to exchange authorisation code - no data received')
        }
      } catch (error) {
        setError(`Integration failed: ${error.message}`)
      } finally {
        setLoading(false)
      }
    }

    handleCallback()
  }, [searchParams, navigate])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Completing SharePoint Integration...</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Please wait while we finalise the connection to your SharePoint site.
          </Typography>
        </>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
            <Typography variant="body1" gutterBottom>
              {error}
            </Typography>
            {error.includes('session') && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>What to do:</strong>
                <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>Close this window</li>
                  <li>Go back to the main Building Passport application</li>
                  <li>Log in again if needed</li>
                  <li>Try the SharePoint integration again</li>
                </ol>
              </Typography>
            )}
            {error.includes('credentials') && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>What to do:</strong>
                <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>Close this window</li>
                  <li>Check your SharePoint Client ID and Client Secret</li>
                  <li>Ensure you're using the secret VALUE, not the secret ID</li>
                  <li>Try the integration again with correct credentials</li>
                </ol>
              </Typography>
            )}
          </Alert>
          <Button
            variant="contained"
            onClick={() => window.close()}
            sx={{ mt: 2 }}
          >
            Close Window
          </Button>
        </>
      ) : (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            SharePoint integration completed successfully!
          </Alert>
          <Typography variant="body2" color="textSecondary">
            You can close this window now.
          </Typography>
        </>
      )}
    </Box>
  )
}

export default SharePointCallback